import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import HomePage from './pages/HomePage';
import PlayPage from './pages/PlayPage';
import EditPage from './pages/EditPage';
import DownloadPage from './pages/DownloadPage';
import { ContentService } from './services/ContentService';

import './App.css';

const STORAGE_KEY = 'h5p_login_data';

interface LoginData {
    username: string;
    name: string;
    email: string;
    csrfToken?: string;
}

const App: React.FC = () => {
    // Get API URL from environment variable
    // Production: https://h5p-api.aienglishhub.vn/h5p
    // Development: /h5p (will be proxied by setupProxy.js)
    // Use REACT_APP_H5P_SERVER_API_URL to match .env.production and other components
    const baseApiUrl = process.env.REACT_APP_H5P_SERVER_API_URL || process.env.REACT_APP_H5P_API_URL;
    const apiUrl = baseApiUrl 
        ? (baseApiUrl.endsWith('/h5p') 
            ? baseApiUrl 
            : baseApiUrl + '/h5p')
        : '/h5p';
    
    const contentService = new ContentService(apiUrl);
    
    // Initialize loggedIn state from localStorage
    const [loggedIn, setLoggedIn] = useState<boolean>(() => {
        // Check if we're in browser environment (not during SSR/build)
        if (typeof window === 'undefined' || !window.localStorage) {
            return false;
        }
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const loginData: LoginData = JSON.parse(stored);
                // Restore CSRF token if available
                if (loginData.csrfToken) {
                    contentService.setCsrfToken(loginData.csrfToken);
                }
                return true;
            }
        } catch (error) {
            console.error('Error loading login data from localStorage:', error);
        }
        return false;
    });

    // Verify session on mount (only once)
    useEffect(() => {
        // Check if we're in browser environment
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }
        
        const verifySession = async () => {
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored) {
                    const loginData: LoginData = JSON.parse(stored);
                    // Try to verify session by calling list API
                    // If it fails, clear localStorage and set loggedIn to false
                    try {
                        await contentService.list();
                        // If successful, restore state
                        if (loginData.csrfToken) {
                            contentService.setCsrfToken(loginData.csrfToken);
                        }
                        setLoggedIn(true);
                    } catch (error) {
                        // Session expired or invalid, clear storage
                        localStorage.removeItem(STORAGE_KEY);
                        contentService.setCsrfToken(undefined);
                        setLoggedIn(false);
                    }
                }
            } catch (error) {
                console.error('Error verifying session:', error);
                if (typeof window !== 'undefined' && window.localStorage) {
                    localStorage.removeItem(STORAGE_KEY);
                }
                setLoggedIn(false);
            }
        };

        verifySession();
    }, []); // Only run once on mount

    return (
        <Router>
            <div className="App">
                <Routes>
                    <Route
                        path="/"
                        element={
                            <HomePage
                                contentService={contentService}
                                loggedIn={loggedIn}
                                onLoggedIn={(loginData?: LoginData) => {
                                    if (loginData && typeof window !== 'undefined' && window.localStorage) {
                                        // Save login data to localStorage
                                        try {
                                            localStorage.setItem(STORAGE_KEY, JSON.stringify(loginData));
                                        } catch (error) {
                                            console.error('Error saving login data to localStorage:', error);
                                        }
                                    }
                                    setLoggedIn(true);
                                }}
                                onLoggedOut={() => {
                                    if (typeof window !== 'undefined' && window.localStorage) {
                                        localStorage.removeItem(STORAGE_KEY);
                                    }
                                    contentService.setCsrfToken(undefined);
                                    setLoggedIn(false);
                                }}
                            />
                        }
                    />
                    <Route
                        path="/play/:contentId"
                        element={<PlayPage contentService={contentService} />}
                    />
                    <Route
                        path="/edit/:contentId"
                        element={<EditPage contentService={contentService} />}
                    />
                </Routes>
            </div>
        </Router>
    );
};

export default App;
